import { useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import {
  BOOKING_STATUS_LABELS,
  OWNER_TIMEZONE,
  SLOT_LENGTHS,
  SLOT_LENGTH_LABELS,
  clientName,
  formatPrice,
  groszToZloty,
  zlotyToGrosz,
  type AvailabilityWindow,
  type Booking,
  type BookingStatus,
  type Client,
  type SlotLength,
  type TimeOff,
} from '@diana/shared'
import { api, ApiError, type CalendarWeek } from '../../lib/api.js'
import { hhmm, timeIn } from '../../lib/datetime.js'
import { Button, ErrorLine, FieldLabel, Rows, Status, type Tone } from '../../classroom/ui.js'
import { Choice, DANGER_LINK, SMALL_BTN, Sheet, SheetSection, hatch } from './calendarUi.js'
import { PlanClassLine } from './PlanSection.js'

type Props = {
  day: string
  hour: number
  windows: AvailabilityWindow[]
  bookings: Booking[]
  /** The plan lessons tied to these bookings. */
  planItems?: CalendarWeek['planItems']
  clients: Client[]
  timeOff: TimeOff[]
  timeOffColor: string
  onClose: () => void
  onChanged: () => void
  onOpenWindow: (startTime: string, endTime: string, slotMinutes: SlotLength) => void
  onDeleteWindow: (id: string) => void
}

const pad = (n: number) => String(n).padStart(2, '0')

const STATUS_TONE: Record<BookingStatus, Tone> = {
  pending: 'wait',
  confirmed: 'info',
  completed: 'ok',
  no_show: 'bad',
  cancelled: 'neutral',
}

/**
 * What opens when Diana clicks an hour in the week view.
 *
 * Three things she might want, in the order she wants them:
 *   1. deal with whoever is already in that hour (confirm a request, mark a
 *      lesson done, fix a price)
 *   2. put one of her own students in
 *   3. open or close the hour for strangers on the public page
 */
export function HourEditor({
  day,
  hour,
  windows,
  bookings,
  planItems = [],
  clients,
  timeOff,
  timeOffColor,
  onClose,
  onChanged,
  onOpenWindow,
  onDeleteWindow,
}: Props) {
  const pretty = new Intl.DateTimeFormat('pl-PL', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    timeZone: 'UTC',
  }).format(new Date(`${day}T12:00:00Z`))

  const hourLabel = `${pad(hour)}:00–${pad(hour + 1)}:00`
  // One gold button per dialog: while a request waits, „Potwierdź” is it.
  const requestWaiting = bookings.some((b) => b.status === 'pending')

  return (
    <Sheet label={`${pretty}, ${hourLabel}`} title={pretty} sub={hourLabel} onClose={onClose}>
      {timeOff.length > 0 && (
        <section className="flex flex-col gap-1 rounded-class-md px-5 py-4 text-class-ink-soft shadow-[inset_0_0_0_1px_var(--color-class-hairline)]" style={hatch(timeOffColor)}>
          <p className="m-0 font-semibold text-class-ink">{timeOff[0]!.label || 'Nie pracujesz'}</p>
          <p className="m-0 text-class-gloss">
            {timeOff.map((t) => `${hhmm(t.startTime)}–${hhmm(t.endTime)}`).join(', ')} · nikt nie zarezerwuje tych godzin. Odblokujesz je w
            widoku tygodnia: zaznacz i wybierz „Wyczyść”.
          </p>
        </section>
      )}

      {bookings.length > 0 && (
        <SheetSection title="W tej godzinie">
          <Rows>
            {bookings.map((b) => (
              <BookingRow key={b.id} booking={b} plan={planItems.filter((p) => p.bookingId === b.id)} clients={clients} onChanged={onChanged} />
            ))}
          </Rows>
        </SheetSection>
      )}

      <LessonForm day={day} hour={hour} clients={clients} onChanged={onChanged} primary={!requestWaiting} />

      <SheetSection title="Zapisy ze strony">
        {windows.length === 0 ? (
          <OpenHour hour={hour} onOpen={onOpenWindow} />
        ) : (
          <>
            <Rows>
              {windows.map((w) => (
                <div key={w.id} className="flex min-h-14 items-center gap-4">
                  <span className="min-w-0 flex-1">
                    <span className="font-semibold">
                      {hhmm(w.startTime)}–{hhmm(w.endTime)}
                    </span>
                    <span className="ml-3 text-class-gloss text-class-ink-soft">
                      lekcje po {SLOT_LENGTH_LABELS[w.slotMinutes as SlotLength] ?? `${w.slotMinutes} min`}
                    </span>
                  </span>
                  <button type="button" onClick={() => onDeleteWindow(w.id)} className={DANGER_LINK}>
                    Zamknij
                  </button>
                </div>
              ))}
            </Rows>
            <p className="m-0 text-class-gloss text-class-ink-soft">Zamknięcie godzin nie odwołuje istniejących zapisów.</p>
          </>
        )}
      </SheetSection>
    </Sheet>
  )
}

/* ── one booking, with the actions that make sense for its status ──────── */

function BookingRow({
  booking,
  plan,
  clients,
  onChanged,
}: {
  booking: Booking
  plan: CalendarWeek['planItems']
  clients: Client[]
  onChanged: () => void
}) {
  const [price, setPrice] = useState(String(groszToZloty(booking.priceGrosz)))
  const [linkTo, setLinkTo] = useState('')

  const patch = useMutation({
    mutationFn: (p: Parameters<typeof api.studio.updateBooking>[1]) => api.studio.updateBooking(booking.id, p),
    onSuccess: onChanged,
  })

  const setStatus = (status: BookingStatus) => patch.mutate({ status })
  // An emptied field is not 0 zł (Number('') is 0): „Zapisz” would have made the lesson free.
  const parsedPrice = price.trim() === '' ? NaN : Number(price.replace(',', '.'))
  const priceValid = Number.isFinite(parsedPrice) && parsedPrice >= 0
  const priceDirty = priceValid && zlotyToGrosz(parsedPrice) !== booking.priceGrosz

  const client = booking.clientId ? clients.find((c) => c.id === booking.clientId) : undefined
  const pending = booking.status === 'pending'
  const priceId = `price-${booking.id}`
  const linkId = `link-${booking.id}`

  return (
    <div className="flex flex-col gap-3 py-4">
      <div className="flex flex-wrap items-start gap-x-4 gap-y-1">
        <span className="min-w-0 flex-1 leading-snug">
          <span className="block font-semibold">
            {client ? (
              <Link to={`/studio/klienci/${client.id}`} className="!text-class-ink hover:!text-class-sky-700">
                {clientName(client)}
              </Link>
            ) : (
              booking.name
            )}
          </span>
          <span className="block text-class-gloss text-class-ink-soft">
            {timeIn(booking.startsAt, OWNER_TIMEZONE)}–{timeIn(booking.endsAt, OWNER_TIMEZONE)}
            {booking.email && (
              <>
                {' · '}
                <a href={`mailto:${booking.email}`}>{booking.email}</a>
              </>
            )}
            {booking.phone && ` · ${booking.phone}`}
          </span>
        </span>
        <Status tone={STATUS_TONE[booking.status]}>{BOOKING_STATUS_LABELS[booking.status]}</Status>
      </div>

      {(booking.goals || booking.level) && (
        <p className="m-0 text-class-gloss">
          {booking.level && <span className="mr-2 font-semibold">{booking.level}</span>}
          {booking.goals}
        </p>
      )}

      {plan.map((p, i) => (
        <PlanClassLine key={`${p.planId}-${p.lessonSlug ?? i}`} item={p} clientId={booking.clientId} />
      ))}

      {pending && (
        <p className="m-0 text-class-gloss text-class-ink-soft">
          Prośba ze strony. Skontaktuj się i ustal stały termin, potem potwierdź albo odrzuć.
        </p>
      )}

      <div className="flex flex-wrap items-end gap-4">
        <div>
          <FieldLabel htmlFor={priceId}>Cena (zł)</FieldLabel>
          <span className="flex items-center gap-2">
            <input id={priceId} inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value)} className="cl-input min-h-10 w-24" />
            {priceDirty && (
              <Button variant="secondary" className={SMALL_BTN} onClick={() => patch.mutate({ priceGrosz: zlotyToGrosz(parsedPrice) })}>
                Zapisz
              </Button>
            )}
          </span>
        </div>

        {!client && clients.length > 0 && (
          <div>
            <FieldLabel htmlFor={linkId}>Powiąż z uczniem</FieldLabel>
            <span className="flex items-center gap-2">
              <select id={linkId} value={linkTo} onChange={(e) => setLinkTo(e.target.value)} className="cl-input min-h-10 w-48">
                <option value="">— wybierz —</option>
                {clients
                  .filter((c) => c.active)
                  .map((c) => (
                    <option key={c.id} value={c.id}>
                      {clientName(c)}
                    </option>
                  ))}
              </select>
              {linkTo && (
                <Button variant="secondary" className={SMALL_BTN} onClick={() => patch.mutate({ clientId: linkTo })}>
                  Powiąż
                </Button>
              )}
            </span>
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        {pending && (
          <>
            <Button className={SMALL_BTN} disabled={patch.isPending} onClick={() => setStatus('confirmed')}>
              Potwierdź
            </Button>
            <Action danger onClick={() => setStatus('cancelled')} disabled={patch.isPending}>
              Odrzuć
            </Action>
          </>
        )}
        {booking.status === 'confirmed' && (
          <>
            <Button variant="secondary" className={SMALL_BTN} disabled={patch.isPending} onClick={() => setStatus('completed')}>
              Odbyta
            </Button>
            <Button variant="secondary" className={SMALL_BTN} disabled={patch.isPending} onClick={() => setStatus('no_show')}>
              Nieobecność
            </Button>
            <Action danger onClick={() => setStatus('cancelled')} disabled={patch.isPending}>
              Odwołaj
            </Action>
          </>
        )}
        {(booking.status === 'completed' || booking.status === 'no_show') && (
          <Action onClick={() => setStatus('confirmed')} disabled={patch.isPending}>
            Cofnij do potwierdzonej
          </Action>
        )}
      </div>

      {patch.isError && <ErrorLine>Nie udało się zapisać.</ErrorLine>}
    </div>
  )
}

function Action({
  children,
  onClick,
  danger,
  disabled,
}: {
  children: ReactNode
  onClick: () => void
  danger?: boolean
  disabled?: boolean
}) {
  return (
    <button type="button" onClick={onClick} disabled={disabled} className={danger ? DANGER_LINK : 'cl-link-quiet text-class-gloss disabled:opacity-50'}>
      {children}
    </button>
  )
}

/* ── put one of her students in ────────────────────────────────────────── */

function LessonForm({
  day,
  hour,
  clients,
  onChanged,
  primary,
}: {
  day: string
  hour: number
  clients: Client[]
  onChanged: () => void
  primary: boolean
}) {
  const active = clients.filter((c) => c.active)
  const [clientId, setClientId] = useState('')
  const [start, setStart] = useState(`${pad(hour)}:00`)
  const [minutes, setMinutes] = useState<SlotLength>(60)
  const [price, setPrice] = useState('')
  const [notes, setNotes] = useState('')

  const create = useMutation({
    mutationFn: () =>
      api.studio.createBooking({
        clientId,
        date: day,
        startTime: start,
        minutes,
        ...(price.trim() ? { priceGrosz: zlotyToGrosz(Number(price.replace(',', '.'))) } : {}),
        notes,
      }),
    onSuccess: () => {
      setNotes('')
      onChanged()
    },
  })

  const chosen = active.find((c) => c.id === clientId)
  const priceNum = Number(price.replace(',', '.'))
  const priceOk = price.trim() === '' || (Number.isFinite(priceNum) && priceNum >= 0)
  const id = `lesson-${day}-${hour}`

  return (
    <SheetSection title="Wpisz lekcję">
      {active.length === 0 ? (
        <p className="m-0 text-class-ink-soft">
          Nie masz jeszcze uczniów. <Link to="/studio/klienci">Dodaj pierwszego</Link>, a potem wpisz go tutaj.
        </p>
      ) : (
        <>
          <div className="grid gap-4 desk:grid-cols-2">
            <div className="desk:col-span-2">
              <FieldLabel htmlFor={`${id}-who`}>Uczeń</FieldLabel>
              <select id={`${id}-who`} value={clientId} onChange={(e) => setClientId(e.target.value)} className="cl-input">
                <option value="">— wybierz ucznia —</option>
                {active.map((c) => (
                  <option key={c.id} value={c.id}>
                    {clientName(c)}
                    {c.tags.length ? ` · ${c.tags.join(', ')}` : ''}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <FieldLabel htmlFor={`${id}-start`}>Początek</FieldLabel>
              <input id={`${id}-start`} type="time" step={900} value={start} onChange={(e) => setStart(e.target.value)} className="cl-input" />
            </div>

            <div>
              <FieldLabel htmlFor={`${id}-price`}>Cena (zł)</FieldLabel>
              <input
                id={`${id}-price`}
                inputMode="decimal"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                placeholder={chosen ? (chosen.defaultPriceGrosz ? formatPrice(chosen.defaultPriceGrosz) : 'z pakietu / stawki') : '—'}
                className="cl-input"
              />
            </div>
          </div>

          <div>
            <p className="cl-label m-0 mb-1.5">Długość</p>
            <Choice
              label="Długość lekcji"
              value={minutes}
              onChange={setMinutes}
              items={SLOT_LENGTHS.map((len) => ({ value: len, label: SLOT_LENGTH_LABELS[len] }))}
            />
          </div>

          <div>
            <FieldLabel htmlFor={`${id}-notes`}>Notatka (opcjonalnie)</FieldLabel>
            <input id={`${id}-notes`} value={notes} onChange={(e) => setNotes(e.target.value)} className="cl-input" />
            <p className="m-0 mt-1.5 text-class-label text-class-ink-faint">Pusta cena = stawka ucznia albo cena za lekcję z jego pakietu.</p>
          </div>

          {create.error instanceof ApiError && (
            <ErrorLine>{create.error.status === 409 ? 'Ten czas jest już zajęty.' : 'Nie udało się wpisać lekcji.'}</ErrorLine>
          )}

          <div className="pt-1">
            <Button variant={primary ? 'primary' : 'secondary'} disabled={!clientId || !priceOk || create.isPending} onClick={() => create.mutate()}>
              {create.isPending ? 'Zapisuję…' : 'Wpisz lekcję'}
            </Button>
          </div>
        </>
      )}
    </SheetSection>
  )
}

/* ── open this hour for the public page ────────────────────────────────── */

function OpenHour({ hour, onOpen }: { hour: number; onOpen: (startTime: string, endTime: string, slotMinutes: SlotLength) => void }) {
  const [slot, setSlot] = useState<SlotLength>(60)
  const end = Math.min(hour + (slot > 60 ? 2 : 1), 24)

  return (
    <>
      <p className="m-0 text-class-ink-soft">Ta godzina jest zamknięta — nikt ze strony nie może jej zarezerwować.</p>
      <Choice
        label="Długość lekcji"
        value={slot}
        onChange={setSlot}
        items={SLOT_LENGTHS.map((len) => ({ value: len, label: SLOT_LENGTH_LABELS[len] }))}
      />
      <div>
        <Button variant="secondary" className={SMALL_BTN} onClick={() => onOpen(`${pad(hour)}:00`, `${pad(end)}:00`, slot)}>
          Otwórz {pad(hour)}:00–{pad(end)}:00 na zapisy
        </Button>
      </div>
    </>
  )
}
